// covered-by: frontends/carrie/src/routes/ask-puntt/hooks/__tests__/useContainerWidth.test.ts
import { type RefObject, useLayoutEffect, useState } from "react";

/**
 * Track the pixel width of a DOM container via ResizeObserver.
 *
 * Returns `null` until the first measurement lands so callers can render a
 * safe fallback (e.g., the legacy flex layout) instead of guessing at a
 * width before paint. After first measure, returns the current container
 * width in pixels and updates on every observed resize.
 *
 * Used by the AskPuntt resize-pane feature: only mount the PanelGroup once
 * the container is known to be >= RESIZE_LAYOUT_MIN_WIDTH_PX (768 px).
 * Narrower viewports fall back to the existing flex layout.
 *
 * Pass `enabled: false` while the ref target is not mounted (e.g. a loading
 * gate that returns before the workspace row renders). When `enabled` flips
 * back to true the effect re-runs and attaches the ResizeObserver.
 */
export function useContainerWidth(
  ref: RefObject<HTMLElement | null>,
  enabled = true,
): number | null {
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const node = ref.current;
    if (!node) return;

    // Seed immediately so callers don't flicker through a null tick when
    // the element is already laid out by the time the effect runs.
    setWidth(Math.round(node.getBoundingClientRect().width));

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const next = Math.round(entry.contentRect.width);
      setWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [ref, enabled]);

  return width;
}
